import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Reveal } from "@/components/ui/Reveal";

export const HowItWorksCycle = ({ steps = [] }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused || steps.length === 0) return undefined;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    timer.current = setInterval(() => {
      setActive((a) => (a + 1) % steps.length);
    }, 4200);
    return () => clearInterval(timer.current);
  }, [paused, steps.length]);

  const current = steps[active] || {};

  return (
    <section
      id="how-it-works"
      className="gm-section"
      style={{ background: "var(--gm-navy-950)" }}
      data-testid="how-it-works-section"
    >
      <div className="gm-container">
        <SectionHeader
          eyebrow="How Goldmap works"
          title="One repeating cycle, no fortune telling"
          subtitle="Every day the EA follows the same six moves. Tap any step to read what happens inside."
          align="center"
          testId="how-it-works-header"
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="min-w-0 lg:col-span-7">
            <ol
              className="space-y-3"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              {steps.map((s, i) => {
                const isActive = i === active;
                return (
                  <li key={s.slug || s.no}>
                    <button
                      type="button"
                      onClick={() => {
                        setActive(i);
                        setPaused(true);
                      }}
                      data-testid={`how-it-works-step-${i + 1}`}
                      aria-current={isActive}
                      className="flex w-full items-center gap-4 rounded-xl p-4 text-left"
                      style={{
                        background: isActive ? "rgba(224,31,38,0.08)" : "rgba(255,255,255,0.03)",
                        border: `1px solid ${
                          isActive ? "rgba(224,31,38,0.42)" : "rgba(255,255,255,0.1)"
                        }`,
                        transition:
                          "background-color 220ms ease, border-color 220ms ease",
                      }}
                    >
                      <span
                        className="gm-ring-num"
                        style={
                          isActive
                            ? { borderColor: "var(--gm-red)", background: "rgba(224,31,38,0.16)" }
                            : undefined
                        }
                      >
                        {s.no}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-white sm:text-base">
                          {s.title}
                        </span>
                        <span
                          className="mt-0.5 block truncate text-xs"
                          style={{ color: "var(--gm-muted-2)" }}
                        >
                          {s.body}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="lg:col-span-5">
            <Reveal>
              <div className="gm-card sticky top-28" data-testid="how-it-works-detail">
                <div className="flex items-center justify-between">
                  <span className="gm-eyebrow">Step {current.no}</span>
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "var(--gm-muted)",
                    }}
                  >
                    <RefreshCw className="h-3 w-3" />
                    loops forever
                  </span>
                </div>
                <h3 className="mt-3 text-xl font-bold text-white">{current.title}</h3>
                <p
                  className="mt-3 text-sm leading-relaxed"
                  style={{ color: "var(--gm-muted)" }}
                >
                  {current.body}
                </p>

                <div
                  className="mt-6 flex items-center gap-1.5"
                  aria-hidden="true"
                >
                  {steps.map((s, i) => (
                    <span
                      key={s.slug || i}
                      className="h-1.5 flex-1 rounded-full"
                      style={{
                        background:
                          i === active ? "var(--gm-red)" : "rgba(255,255,255,0.14)",
                        transition: "background-color 220ms ease",
                      }}
                    />
                  ))}
                </div>
                <p className="mt-4 text-xs" style={{ color: "var(--gm-muted-2)" }}>
                  The EA does not read news or predict direction. It only reacts to its
                  own rules and the limits you set.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
